import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { 
  Drama,
  Club,
  Lock,
  Boxes,
  ChevronLeft,
  ChevronRight,
  Bot,
  Library,
  Coins,
  Gem,
  Menu,
  CreditCard,
} from "lucide-react"
import { toast } from "@/hooks/useToast"
import { useUser } from "@clerk/clerk-react"
import { useState, useEffect } from "react"
import api from "@/services/api"
import { formatLargeNumber } from "@/lib/utils/formatters"
import { useBreakpoint } from "@/providers/BreakpointProvider"
import { useSidebar } from "@/components/ui/sidebar"

interface SidebarProps {
  onCreateCard: () => void;
  onClose?: () => void;
}

interface TokenBalance {
  remaining: number
  total: number
  resetIn: number
  tier: string
}

export function Sidebar({ onCreateCard, onClose }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useUser()
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [tokenBalance, setTokenBalance] = useState<TokenBalance | null>(null)
  const { isMobile } = useBreakpoint()
  const { setOpen } = useSidebar()

  useEffect(() => {
    const fetchTokenBalance = async () => {
      try {
        const response = await api.get('/api/v1/tokens/balance')
        setTokenBalance(response.data.data)
      } catch (error) {
        console.error('Error fetching token balance:', error)
      }
    }

    fetchTokenBalance()
  }, [])

  useEffect(() => {
    if (!isMobile) {
      setIsCollapsed(false)
    }
  }, [isMobile])

  const handleNavigation = (path: string, isLocked: boolean, isExternal: boolean) => {
    if (isLocked) {
      toast({
        variant: "destructive",
        title: "Feature Locked",
        description: "This feature is coming soon!",
      })
      return
    }

    if (isExternal) {
      window.open(path, '_blank')
      return
    }

    if (path === '/create') {
      onCreateCard()
    }
    
    navigate(path)
    
    // Close sidebar on mobile after navigation
    if (isMobile) {
      setOpen(false)
      onClose?.()
    }
  }

  const mainNavigationItems = [
    { 
      icon: <Gem className="h-6 w-6" />, 
      label: 'Game Genesis',
      path: '/create-game',
      isLocked: false,
      isExternal: false
    },
    { 
      icon: <Library className="h-6 w-6" />, 
      label: 'Decks',
      path: '/decks',
      isLocked: false,
      isExternal: false
    },
    { 
      icon: <Boxes className="h-6 w-6" />, 
      label: 'Blueprints',
      path: '/blueprints',
      isLocked: false,
      isExternal: false
    },
    { 
      icon: <Club className="h-6 w-6" />, 
      label: 'Card Roulette',
      path: '/create',
      isLocked: false,
      isExternal: false
    },
    { 
      icon: <Drama className="h-6 w-6" />, 
      label: 'Showcase',
      path: '/feed',
      isLocked: false,
      isExternal: false
    },
  ]

  const bottomNavigationItems = [
    {
      icon: <Bot className="h-6 w-6" />, 
      label: 'Discord Server',
      path: 'https://discord.gg/6tVc2wBHNH',
      isLocked: false,
      isExternal: true
    },
    {
      icon: <CreditCard className="h-6 w-6" />,
      label: 'Pricing and Support',
      path: '/pricing',
      isLocked: false,
      isExternal: false
    }
  ]

  const renderNavigationItems = () => (
    <>
      <div className="flex-1 space-y-1">
        {mainNavigationItems.map((item, index) => (
          <Button
            key={index}
            variant="ghost"
            className={`w-full justify-start gap-4 text-muted-foreground relative ${
              item.path === location.pathname ? "bg-accent text-accent-foreground" : ""
            } ${item.isLocked ? "opacity-60 cursor-not-allowed hover:bg-background" : ""}`}
            onClick={() => handleNavigation(item.path, item.isLocked, item.isExternal)}
          >
            {item.icon}
            <span className={!isMobile && isCollapsed ? "hidden" : "block"}>{item.label}</span>
            {item.isLocked && (
              <Lock className={`h-4 w-4 ${!isMobile && isCollapsed ? "relative" : "absolute right-4"}`} />
            )}
          </Button>
        ))}
      </div>

      <div className="border-t border-border pt-4 mt-auto space-y-2">
        {tokenBalance && (
          <Button
            variant="ghost"
            className={`w-full justify-start gap-4 text-muted-foreground ${!isMobile && isCollapsed ? "justify-center" : ""}`}
          >
            <Coins className="h-6 w-6 text-tokens" />
            {(!isMobile && !isCollapsed || isMobile) && (
              <span className="text-sm font-medium">
                {formatLargeNumber(tokenBalance.remaining)} Tokens
              </span>
            )}
          </Button>
        )}

        {bottomNavigationItems.map((item, index) => (
          <Button
            key={index}
            variant="ghost"
            className={`w-full justify-start gap-4 text-muted-foreground relative ${
              item.path === location.pathname ? "bg-accent" : ""
            }`}
            onClick={() => handleNavigation(item.path, item.isLocked, item.isExternal)}
          >
            {item.icon}
            <span className={!isMobile && isCollapsed ? "hidden" : "block"}>{item.label}</span>
          </Button>
        ))}

        <Button
          variant="ghost"
          className={`w-full justify-start gap-4 text-muted-foreground ${
            location.pathname.includes('/profile') ? "bg-accent" : ""
          }`}
          onClick={() => handleNavigation(`/profile/${user?.username}`, false, false)}
        >
          <Avatar className="h-6 w-6">
            <AvatarImage src={user?.imageUrl} alt={user?.username || ''} />
            <AvatarFallback>{user?.firstName?.[0] || user?.username?.[0]}</AvatarFallback>
          </Avatar>
          {(!isMobile && !isCollapsed || isMobile) && (
            <span className="truncate">
              {user?.username || 'Profile'}
            </span>
          )}
        </Button>
      </div>
    </>
  )

  if (isMobile) {
    return (
      <div id="sidebar"
        className="flex flex-col h-full w-full glass-effect-darker overflow-y-auto border-r
                   motion-safe:transition-all motion-safe:duration-200"
      >
        <div className="flex items-center p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10"
          >
            <Menu className="h-6 w-6 text-muted-foreground" />
          </Button>
          <h1 className="text-3xl font-bold px-4 font-instrument text-logo">
            Dekk
          </h1>
        </div>
        <div className="flex-1 px-4">
          {renderNavigationItems()}
        </div>
      </div>
    )
  }

  return (
    <div 
      className={`hidden md:flex text-foreground flex-col fixed left-0 top-0 h-screen 
                  glass-effect-darker border-r p-4 gap-4 z-10
                  motion-safe:transition-all motion-safe:duration-300
                  ${isCollapsed ? "w-20" : "w-64"}`}
    >
      <div className="flex items-center justify-between py-4">
        <h1 className={`text-3xl font-bold px-4 font-instrument ${isCollapsed ? "hidden" : "block"} text-logo`}>
          Dekk
        </h1>
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ml-auto"
          >
            {isCollapsed ? (
              <ChevronRight className="h-6 w-6" />
            ) : (
              <ChevronLeft className="h-6 w-6" />
            )}
          </Button>
        )}
      </div>
      {renderNavigationItems()}
    </div>
  )
} 